import { map } from 'lodash-es';
import * as C from '../../constants';
import SkiRentalService from '../../../../services/ski/ski-rental-service';
import SkiService from '../../../../services/ski/ski-service';
import exceptionValidator from '../../../../utils/exceptionValidator';

export default {

  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_RENTAL_RESET_STATE);
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  seasonsLoad({ commit }) {
    commit(C.SKI_RENTAL_SEASONS_LOAD_PENDING);
    return SkiRentalService.getSeasonsWithRelations()
      .then(res => {
        commit(C.SKI_RENTAL_SEASONS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_SEASONS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<void>|Promise<T>}
   */
  seasonByIdLoad({ commit, state }, seasonId) {
    commit(C.SKI_RENTAL_SEASON_LOAD_PENDING);
    if (seasonId === undefined) {
      const newSeasonItem = Object.assign({}, state.season.newSeasonItem);
      commit(C.SKI_RENTAL_SEASON_LOAD_SUCCESS, newSeasonItem);
      return Promise.resolve();
    }
    return SkiRentalService.getSeasonById(seasonId)
      .then(res => {
        commit(C.SKI_RENTAL_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<T>}
   */
  seasonWithRelationsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_SEASON_LOAD_PENDING);
    return SkiRentalService.getSeasonWithRelationsBySeasonId(seasonId)
      .then(res => {
        commit(C.SKI_RENTAL_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setSeason({ commit }, payload) {
    commit(C.SKI_RENTAL_SEASON_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  seasonCreate({ commit, dispatch }, payload) {
    commit(C.SKI_RENTAL_SEASON_CREATE_PENDING);
    return SkiRentalService.createSeason(payload)
      .then(response => {
        const skiRentalSeasonId = response.data.id;
        const newSeasonItem = Object.assign({}, payload);
        newSeasonItem.id = skiRentalSeasonId;
        commit(C.SKI_RENTAL_SEASON_CREATE_SUCCESS, newSeasonItem);
        dispatch('seasonByIdLoad', newSeasonItem.id);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_SEASON_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  seasonSave({ commit }, payload) {
    commit(C.SKI_RENTAL_SEASON_SAVE_PENDING);
    return SkiRentalService.saveSeason(payload)
      .then(() => {
        commit(C.SKI_RENTAL_SEASON_SAVE_SUCCESS, payload);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_SEASON_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<void>|Promise<T>}
   */
  periodDatesByIdLoad({ commit, state }, seasonId) {
    commit(C.SKI_RENTAL_PERIOD_DATES_LOAD_PENDING);
    if (seasonId === undefined) {
      const newPeriodDateItem = Object.assign({}, state.season.periodDates.newPeriodItem);
      newPeriodDateItem.isActive = false;
      const items = Object.values(state.season.periodDates.items)
        .map(
          periodDate => (periodDate.id === newPeriodDateItem.id ? newPeriodDateItem : periodDate),
        );
      commit(C.SKI_RENTAL_PERIOD_DATES_LOAD_SUCCESS, items);
      return Promise.resolve();
    }
    return SkiRentalService.getPeriodDatesBySeasonId(seasonId)
      .then(res => {
        commit(C.SKI_RENTAL_PERIOD_DATES_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_RENTAL_PERIOD_DATES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_RENTAL_PERIOD_DATES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  periodDateAddNew({ commit }) {
    commit(C.SKI_RENTAL_PERIOD_DATE_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setPeriodDateItem({ commit }, payload) {
    commit(C.SKI_RENTAL_PERIOD_DATES_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  periodDateCreate({ commit, dispatch }, payload) {
    commit(C.SKI_RENTAL_PERIOD_DATE_CREATE_PENDING);
    return SkiRentalService.createPeriodDate(payload.skiRentalSeasonId, payload)
      .then(() => {
        commit(C.SKI_RENTAL_PERIOD_DATE_CREATE_SUCCESS);
        dispatch('periodDatesByIdLoad', payload.skiRentalSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_PERIOD_DATE_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param params
   * @returns {Promise<void>|Promise<T>}
   */
  periodDateRemove({ commit }, params) {
    if (!params.periodId) {
      commit(C.SKI_RENTAL_PERIOD_DATE_DELETE_PENDING, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_RENTAL_PERIOD_DATE_DELETE_PENDING);
    return SkiRentalService.deletePeriodDate(params.seasonId, params.periodId)
      .then(() => {
        commit(C.SKI_RENTAL_PERIOD_DATE_DELETE_SUCCESS, params.periodId);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_PERIOD_DATE_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param params
   * @returns {Promise<void>|Promise<T>}
   */
  periodDateClone({ commit }, params) {
    if (!params.periodId) {
      commit(C.SKI_RENTAL_PERIOD_DATE_CLONE_PENDING, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_RENTAL_PERIOD_DATE_CLONE_PENDING);
    return SkiRentalService.clonePeriodDate(params.seasonId, params.periodId)
      .then(() => {
        commit(C.SKI_RENTAL_PERIOD_DATE_CLONE_SUCCESS, params.periodId);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_PERIOD_DATE_CLONE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param params
   * @returns {Promise<void>|Promise<T>}
   */
  periodDateUpdate({ commit }, payload) {
    commit(C.SKI_RENTAL_PERIOD_DATE_UPDATE_PENDING);
    return SkiRentalService.updatePeriodDate(payload)
      .then(() => {
        commit(C.SKI_RENTAL_PERIOD_DATE_UPDATE_SUCCESS, payload);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_PERIOD_DATE_UPDATE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  salesTypesLoad({ commit }) {
    commit(C.SKI_RENTAL_SALES_TYPES_LOAD_PENDING);
    return SkiRentalService.getSalesTypes()
      .then(res => {
        commit(C.SKI_RENTAL_SALES_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_SALES_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  levelsLoad({ commit }) {
    commit(C.SKI_RENTAL_LEVELS_LOAD_PENDING);
    return SkiRentalService.getSkiRentalLevels()
      .then(res => {
        commit(C.SKI_RENTAL_LEVELS_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_LEVELS_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  packagesLoad({ commit }) {
    commit(C.SKI_RENTAL_PACKAGES_LOAD_PENDING);
    return SkiRentalService.getSkiRentalPackages()
      .then(res => {
        commit(C.SKI_RENTAL_PACKAGES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_PACKAGES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  skiTypesLoad({ commit }) {
    commit(C.SKI_RENTAL_SKI_TYPES_LOAD_PENDING);
    return SkiRentalService.getSkiRentalSkiTypes()
      .then(res => {
        commit(C.SKI_RENTAL_SKI_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_SKI_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  possiblePriceCombinationsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_PRICE_COMBINATIONS_LOAD_PENDING);
    return SkiRentalService.getPossiblePriceCombinationsBySeasonId(seasonId)
      .then(res => {
        const possiblePriceCombinations = res.data.map((priceCombination, index) => ({
          level: priceCombination.level,
          package: priceCombination.package,
          skiType: priceCombination.skiType,
          personCategory: priceCombination.personCategory,
          index,
        }));
        commit(C.SKI_RENTAL_PRICE_COMBINATIONS_LOAD_SUCCESS, possiblePriceCombinations);
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_RENTAL_PRICE_COMBINATIONS_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_RENTAL_PRICE_COMBINATIONS_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationPersonCategory({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_PERSON_CATEGORY_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationLevel({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_LEVEL_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationPackage({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_PACKAGE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationSkiType({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_SKI_TYPE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationOnlyShowSavedPrices({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_ONLY_SHOW_SAVED_PRICES_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceCombinationOnlyShowPricesMissingXmlChannelProducts({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_ONLY_SHOW_XML_MAPPING_MISSING, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setFiltersClear({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_COMBINATION_CLEAR_FILTERS_SET, payload);
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  personCategoriesByIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_PERSON_CATEGORIES_LOAD_PENDING);
    return SkiRentalService.getPersonCategoriesById(seasonId)
      .then(res => {
        commit(C.SKI_RENTAL_PERSON_CATEGORIES_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_RENTAL_PERSON_CATEGORIES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_RENTAL_PERSON_CATEGORIES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  discountsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_DISCOUNTS_LOAD_PENDING);
    return SkiRentalService.getDiscountsBySeasonId(seasonId)
      .then(res => {
        const discounts = map(res.data, discount => ({
          id: discount.id,
          skiRentalSeasonId: discount.skiRentalSeasonId,
          bookFrom: discount.bookFrom,
          bookTo: discount.bookTo,
          arrivalFrom: discount.arrivalFrom,
          arrivalTo: discount.arrivalTo,
          minPax: discount.minPax,
          maxPax: discount.maxPax,
          percentage: discount.percentage,
          personCategories: discount.personCategories,
        }));
        commit(C.SKI_RENTAL_DISCOUNTS_LOAD_SUCCESS, discounts);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_DISCOUNTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  areasBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_AREAS_LOAD_PENDING);
    return SkiRentalService.getAreasBySeasonId(seasonId)
      .then(res => {
        commit(C.SKI_RENTAL_AREAS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_AREAS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param newItem
   * @returns {Promise<T>}
   */
  areaOnSeasonSave({ commit, dispatch }, { seasonId, newItem }) {
    commit(C.SKI_RENTAL_AREAS_SAVE_PENDING);
    return SkiRentalService.saveAreaOnSeason(seasonId, newItem.id)
      .then(() => {
        commit(C.SKI_RENTAL_AREAS_SAVE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_AREAS_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param areaId
   * @returns {Promise<T>}
   */
  areaOnSeasonDelete({ commit, dispatch }, { seasonId, areaId }) {
    commit(C.SKI_RENTAL_AREAS_DELETE_PENDING);
    return SkiRentalService.deleteAreaOnSeason(seasonId, areaId)
      .then(() => {
        commit(C.SKI_RENTAL_AREAS_DELETE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_RENTAL_AREAS_DELETE_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  suppliersLoad({ commit }) {
    commit(C.SKI_SUPPLIERS_LOAD_PENDING);
    return SkiService.getActiveSkiSuppliers()
      .then(res => {
        commit(C.SKI_SUPPLIERS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_SUPPLIERS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   */
  personCategoryAddNew({ commit }) {
    commit(C.SKI_RENTAL_PERSON_CATEGORY_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setPersonCategory({ commit }, payload) {
    commit(C.SKI_RENTAL_PERSON_CATEGORIES_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  personCategoryCreate({ commit, dispatch }, payload) {
    commit(C.SKI_RENTAL_PERSON_CATEGORY_CREATE_PENDING);
    return SkiRentalService.createPersonCategory(payload)
      .then(() => {
        commit(C.SKI_RENTAL_PERSON_CATEGORY_CREATE_SUCCESS);
        dispatch('personCategoriesByIdLoad', payload.skiRentalSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_PERSON_CATEGORY_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  personCategorySave({ commit, dispatch }, payload) {
    commit(C.SKI_RENTAL_PERSON_CATEGORY_SAVE_PENDING);
    return SkiRentalService.savePersonCategory(payload)
      .then(() => {
        commit(C.SKI_RENTAL_PERSON_CATEGORY_SAVE_SUCCESS, payload);
        dispatch('personCategoriesByIdLoad', payload.skiRentalSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_PERSON_CATEGORY_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param personCategoryId
   * @returns {Promise<void>|Promise<T>}
   */
  personCategoryRemove({ commit }, personCategoryId) {
    if (!personCategoryId) {
      commit(C.SKI_RENTAL_PERSON_CATEGORY_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_RENTAL_PERSON_CATEGORY_REMOVE_PENDING);
    return SkiRentalService.deletePersonCategory(personCategoryId)
      .then(() => {
        commit(C.SKI_RENTAL_PERSON_CATEGORY_REMOVE_SUCCESS, personCategoryId);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_PERSON_CATEGORY_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   */
  discountAddNew({ commit }) {
    commit(C.SKI_RENTAL_DISCOUNT_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setDiscount({ commit }, payload) {
    commit(C.SKI_RENTAL_DISCOUNT_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  discountCreate({ commit, dispatch }, payload) {
    commit(C.SKI_RENTAL_DISCOUNT_CREATE_PENDING);
    return SkiRentalService.createDiscount(payload)
      .then(() => {
        commit(C.SKI_RENTAL_DISCOUNT_CREATE_SUCCESS);
        dispatch('discountsBySeasonIdLoad', payload.skiRentalSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_DISCOUNT_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  discountSave({ commit }, payload) {
    commit(C.SKI_RENTAL_DISCOUNT_SAVE_PENDING);
    return SkiRentalService.saveDiscount(payload)
      .then(() => {
        commit(C.SKI_RENTAL_DISCOUNT_SAVE_SUCCESS, payload);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_DISCOUNT_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param discountId
   * @returns {Promise<void>|Promise<T>}
   */
  discountRemove({ commit }, discountId) {
    if (!discountId) {
      commit(C.SKI_RENTAL_DISCOUNT_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_RENTAL_DISCOUNT_REMOVE_PENDING);
    return SkiRentalService.deleteDiscount(discountId)
      .then(() => {
        commit(C.SKI_RENTAL_DISCOUNT_REMOVE_SUCCESS, discountId);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_DISCOUNT_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param discountId
   * @param newItem
   * @returns {Promise<T>}
   */
  personCategoryOnDiscountSave({ commit, state }, { discountId, newItem }) {
    commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_SAVE_PENDING);
    return SkiRentalService.savePersonCategoryOnDiscount(discountId, newItem.id)
      .then(() => {
        const items = [ ...state.season.discounts.items ];
        items.splice(0, 0, newItem);
        commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_SAVE_SUCCESS);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_SAVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param discountId
   * @param personCategoryId
   * @returns {Promise<T>}
   */
  personCategoryOnDiscountDelete({ commit, state }, { discountId, personCategoryId }) {
    commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_DELETE_PENDING);
    return SkiRentalService.deletePersonCategoryOnDiscount(discountId, personCategoryId)
      .then(() => {
        const index = Object.values(state.season.discounts.items).findIndex(
          item => item.id === discountId,
        );
        const items = [ ...state.season.discounts.items ];
        items.splice(index, 1, items);
        commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_DELETE_SUCCESS);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_DISCOUNT_PERSON_CATEGORY_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  pricesWithDaysBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_RENTAL_PRICES_LOAD_PENDING);
    return SkiRentalService.getPricesWithDaysBySeasonId(seasonId)
      .then(res => {
        if (res.status === 204) {
          commit(C.SKI_RENTAL_PRICES_LOAD_SUCCESS, []);
        } else {
          commit(C.SKI_RENTAL_PRICES_LOAD_SUCCESS, res.data);
        }
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_RENTAL_PRICES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_RENTAL_PRICES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setPrice({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_SET, payload);
  },

  /**
   * @param commit
   * @param payload
   */
  setPriceDay({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_DAY_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  priceSave({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_SAVE_PENDING);
    return SkiRentalService.updatePriceWithDays(payload)
      .then(() => {
        commit(C.SKI_RENTAL_PRICE_SAVE_SUCCESS);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_RENTAL_PRICE_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<void>|Promise<T>}
   */
  priceRemove({ commit }, payload) {
    commit(C.SKI_RENTAL_PRICE_REMOVE_PENDING);
    if (!payload.priceId) {
      commit(C.SKI_RENTAL_PRICE_REMOVE_SUCCESS, payload);
      return Promise.resolve();
    }
    return SkiRentalService.deletePrice(payload.seasonId, payload.priceId)
      .then(() => {
        commit(C.SKI_RENTAL_PRICE_REMOVE_SUCCESS, payload);
      })
      .catch(error => {
        commit(C.SKI_RENTAL_PRICE_REMOVE_FAILURE, error.response.data);
      });
  },

  getSeasonSkiRentalPickupPlaces({ commit, state }) {
    commit(C.SKI_RENTAL_SEASON_PICK_UP_PLACES_PENDING);
    const seasonId = state.season.id;
    return SkiService.getSkiRentalPickupPlaces(seasonId).then(res => {
      commit(C.SKI_RENTAL_SEASON_PICK_UP_PLACES_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_RENTAL_SEASON_PICK_UP_PLACES_FAILURE);
    });
  },

  updateSeasonRentalPickupPlaces({ state, dispatch }, ids) {
    const seasonId = state.season.id;
    return SkiService.updateSkiRentalPickupPlaces({ seasonId, ids }).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Saved ski rental pickup places',
      }, { root: true });
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to save ski rental pickup places',
      }, { root: true });
    });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/rental/actions.js